"use client";

import { useEffect, useState } from "react";
import Icon from "@/components/ui/AppIcon";

export default function ContactForm() {
  const [isHydrated, setIsHydrated] = useState(false);

  useEffect(() => {
    setIsHydrated(true);

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("animate");
          }
        });
      },
      { threshold: 0.1, rootMargin: "0px 0px -5% 0px" }
    );

    document.querySelectorAll(".animate-on-scroll").forEach((el) => {
      observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Form submission logic here
  };

  const contactInfo = [
    {
      id: "contact_phone",
      icon: "PhoneIcon",
      label: "Phone",
      value: "+91 9038809481",
      link: "tel:+919038809481",
    },
    {
      id: "contact_email",
      icon: "EnvelopeIcon",
      label: "Email",
      value: "info@sunandaentertainment.com",
      link: "mailto:info@sunandaentertainment.com",
    },
    {
      id: "contact_address",
      icon: "MapPinIcon",
      label: "Address",
      value: " B-101, First Floor R G City Center Plot No 4 D B Gupta Road Motia Khan, New Delhi, Pahar Ganj, Delhi, India, 11005",
      link: "#",
    },
    {
      id: "contact_hours",
      icon: "ClockIcon",
      label: "Business Hours",
      value: "Mon-Fri: 9:00 AM - 6:00 PM",
      link: "#",
    },
  ];

  return (
    <section className="py-24 md:py-32 px-6 md:px-12">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          {/* Contact Form */}
          <div className="lg:col-span-7 animate-on-scroll [animation:animationIn_0.8s_ease-out_0.2s_both]">
            <div className="glass-card p-8 md:p-10 rounded-3xl">
              <h2 className="text-2xl font-display font-medium text-white mb-2">
                Send Us a Message
              </h2>
              <p className="text-muted-foreground mb-8">
                Fill out the form below and we'll get back to you within 24 hours.
              </p>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div>
                    <label
                      htmlFor="name"
                      className="block text-sm font-medium text-white mb-2"
                    >
                      Your Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      required
                      className="w-full h-12 bg-white/5 border border-white/10 rounded-xl px-4 text-white placeholder-muted-foreground focus:outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/50 transition-all"
                      placeholder="John Doe"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="email"
                      className="block text-sm font-medium text-white mb-2"
                    >
                      Email Address *
                    </label>
                    <input
                      type="email"
                      id="email"
                      required
                      className="w-full h-12 bg-white/5 border border-white/10 rounded-xl px-4 text-white placeholder-muted-foreground focus:outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/50 transition-all"
                      placeholder="john@example.com"
                    />
                  </div>
                </div>

                <div>
                  <label
                    htmlFor="phone"
                    className="block text-sm font-medium text-white mb-2"
                  >
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    className="w-full h-12 bg-white/5 border border-white/10 rounded-xl px-4 text-white placeholder-muted-foreground focus:outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/50 transition-all"
                    placeholder="+91 98765 43210"
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div>
                    <label
                      htmlFor="projectType"
                      className="block text-sm font-medium text-white mb-2"
                    >
                      Project Type *
                    </label>
                    <select
                      id="projectType"
                      required
                      className="w-full h-12 bg-white/5 border border-white/10 rounded-xl px-4 text-white focus:outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/50 transition-all"
                    >
                      <option value="" className="bg-card text-muted-foreground">
                        Select a type
                      </option>
                      <option value="film" className="bg-card text-white">
                        Film Production
                      </option>
                      <option value="event" className="bg-card text-white">
                        Event Management
                      </option>
                      <option value="post" className="bg-card text-white">
                        Post-Production
                      </option>
                      <option value="other" className="bg-card text-white">
                        Other
                      </option>
                    </select>
                  </div>
                  <div>
                    <label
                      htmlFor="budget"
                      className="block text-sm font-medium text-white mb-2"
                    >
                      Budget Range
                    </label>
                    <select
                      id="budget"
                      className="w-full h-12 bg-white/5 border border-white/10 rounded-xl px-4 text-white focus:outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/50 transition-all"
                    >
                      <option value="" className="bg-card text-muted-foreground">
                        Select a range
                      </option>
                      <option value="5k-15k" className="bg-card text-white">
                        $5,000 - $15,000
                      </option>
                      <option value="15k-50k" className="bg-card text-white">
                        $15,000 - $50,000
                      </option>
                      <option value="50k+" className="bg-card text-white">
                        $50,000+
                      </option>
                    </select>
                  </div>
                </div>

                <div>
                  <label
                    htmlFor="message"
                    className="block text-sm font-medium text-white mb-2"
                  >
                    Project Details *
                  </label>
                  <textarea
                    id="message"
                    required
                    rows={6}
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-muted-foreground focus:outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/50 transition-all resize-none"
                    placeholder="Tell us about your project..."
                  ></textarea>
                </div>

                <button
                  type="submit"
                  className="w-full h-14 bg-gradient-to-r from-primary to-red-800 text-white font-medium rounded-xl hover:shadow-lg transition-all duration-300 glow-primary hover:glow-primary-hover flex items-center justify-center gap-2"
                >
                  <Icon name="PaperAirplaneIcon" size={20} />
                  Send Message
                </button>
              </form>
            </div>
          </div>

          {/* Contact Info Sidebar */}
          <div className="lg:col-span-5 animate-on-scroll [animation:animationIn_0.8s_ease-out_0.3s_both]">
            <div className="glass-card p-8 rounded-3xl mb-8">
              <h3 className="text-xl font-display font-medium text-white mb-6">
                Contact Information
              </h3>

              <div className="space-y-6">
                {contactInfo.map((info) => (
                  <a
                    key={info.id}
                    href={info.link}
                    className="flex items-start gap-4 group"
                  >
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-primary flex-shrink-0 group-hover:bg-primary/20 transition-colors">
                      <Icon name={info.icon as any} size={20} />
                    </div>
                    <div>
                      <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">
                        {info.label}
                      </p>
                      <p className="text-white font-medium group-hover:text-primary transition-colors">
                        {info.value}
                      </p>
                    </div>
                  </a>
                ))}
              </div>
            </div>

            {/* Response Time Badge */}
            <div className="glass-card p-6 rounded-2xl border-2 border-primary/30">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center">
                  <Icon name="ClockIcon" size={24} className="text-primary" />
                </div>
                <div>
                  <p className="text-white font-semibold">Quick Response</p>
                  <p className="text-xs text-muted-foreground">
                    We respond within 24 hours
                  </p>
                </div>
              </div>
              <p className="text-sm text-muted-foreground">
                Our team is committed to providing timely responses to all inquiries.
                Expect a personalized reply within one business day.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}